"use client";

import { useRef } from "react";
import { motion, useReducedMotion, useInView } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { Check, ShieldCheck, Laptop, Globe } from "@/components/icons";
import { ACCENT_COLOR } from "./data";

const BULLETS = [
  "Replaces your IP with the proxy's for the destinations you configure it to use.",
  "Can route traffic through a different country or city.",
  "Distributes requests across many IPs to collect data reliably at scale.",
];

export default function QuickAnswer() {
  const reduced = useReducedMotion() ?? false;

  return (
    <section
      aria-labelledby="quick-answer-heading"
      className="border-border py-section-sm relative border-b"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-14">
          <motion.div
            initial={reduced ? false : { opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={scrollRevealViewport}
            transition={{ duration: 0.5 }}
          >
            <p className="text-muted-foreground mb-3 text-xs font-semibold">Quick answer</p>
            <h2
              id="quick-answer-heading"
              className="text-foreground mb-6 scroll-mt-40 text-4xl font-bold tracking-tight sm:text-5xl"
            >
              What&apos;s a proxy server?
            </h2>
            <p className="text-muted-foreground mb-7 text-lg leading-relaxed">
              A proxy server is an intermediary between a client and a destination. Traffic
              configured to use the proxy reaches the destination through the proxy&apos;s network
              connection, so the destination normally sees the proxy&apos;s exit IP instead of the
              client&apos;s public IP. A proxy does not automatically encrypt every application,
              change browser state, or guarantee anonymity.
            </p>
            <ul className="flex flex-col gap-3.5">
              {BULLETS.map((b) => (
                <li
                  key={b}
                  className="text-muted-foreground flex items-start gap-3 text-base leading-relaxed"
                >
                  <Check
                    size={18}
                    className="mt-1 shrink-0"
                    style={{ color: "var(--brand-green)" }}
                    aria-hidden="true"
                  />
                  {b}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={reduced ? false : { opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={scrollRevealViewport}
            transition={{ duration: 0.55 }}
            className="relative"
          >
            <ProxyNodeVisual reduced={reduced} />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// Packet stream timings (s) — distinct values double as stable React keys.
const PACKET_DELAYS = [0, 0.85, 1.7];

// Isometric "proxy node" in the homepage product-card idiom: blueprint dotted grid,
// ambient glow, a preserve-3d slab that floats on a looping z-bob, plus a live
// You → Proxy → Target connection with packets routed through the node.
function ProxyNodeVisual({ reduced }: { reduced: boolean }) {
  const rootRef = useRef<HTMLDivElement>(null);
  const inView = useInView(rootRef, { margin: "100px 0px" });
  const active = !reduced && inView;

  return (
    <div
      ref={rootRef}
      className="bg-background relative h-[360px] w-full overflow-hidden"
      aria-hidden="true"
    >
      {/* Blueprint dotted grid — faded toward the edges so it blends into the page */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(color-mix(in srgb, var(--foreground) 6%, transparent) 1px, transparent 1px)",
          backgroundSize: "20px 20px",
          maskImage: "radial-gradient(ellipse 80% 80% at 50% 50%, #000 35%, transparent 92%)",
          WebkitMaskImage: "radial-gradient(ellipse 80% 80% at 50% 50%, #000 35%, transparent 92%)",
        }}
      />

      {/* Ambient glow behind the node */}
      <div
        className="pointer-events-none absolute top-1/2 left-1/2 mt-[-90px] ml-[-130px] h-[180px] w-[260px] rounded-full opacity-60 blur-3xl"
        style={{ background: `radial-gradient(closest-side, ${ACCENT_COLOR}1A, transparent)` }}
      />

      {/* ── Direct route: You → Target, skipping the proxy (real IP exposed) ── */}
      <svg
        className="pointer-events-none absolute inset-0 h-full w-full"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <motion.path
          d="M 9 50 C 32 9, 68 9, 91 50"
          fill="none"
          stroke="rgba(248,113,113,0.5)"
          strokeWidth={1}
          strokeDasharray="3 4"
          vectorEffect="non-scaling-stroke"
          initial={{ strokeDashoffset: 0 }}
          animate={active ? { strokeDashoffset: -14 } : undefined}
          transition={{ duration: 1.1, repeat: Infinity, ease: "linear" }}
        />
      </svg>
      <span
        className="absolute top-7 left-1/2 z-20 -translate-x-1/2 rounded-full border px-2.5 py-0.5 text-[10px] font-medium whitespace-nowrap"
        style={{
          color: "rgba(248,113,113,0.95)",
          borderColor: "rgba(248,113,113,0.3)",
          background: "rgba(248,113,113,0.08)",
        }}
      >
        Without proxy
      </span>

      {/* "With proxy" label for the centered route */}
      <span
        className="absolute bottom-7 left-1/2 z-20 -translate-x-1/2 rounded-full border px-2.5 py-0.5 text-[10px] font-medium whitespace-nowrap"
        style={{
          color: "var(--brand-green)",
          borderColor: `${ACCENT_COLOR}40`,
          background: `${ACCENT_COLOR}12`,
        }}
      >
        With proxy
      </span>

      {/* ── Connection layer: You ── Proxy ── Target ─────────────────────── */}
      {/* Wire */}
      <div
        className="absolute top-1/2 right-[72px] left-[72px] h-px -translate-y-1/2"
        style={{
          background: `linear-gradient(90deg, transparent, ${ACCENT_COLOR}55, transparent)`,
        }}
      />
      {/* Traveling packets — disappear behind the slab and re-emerge: "through" it */}
      <div className="absolute top-1/2 right-[72px] left-[72px] h-px -translate-y-1/2">
        {PACKET_DELAYS.map((delay) => (
          <motion.span
            key={`pkt-${delay}`}
            className="absolute top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full"
            style={{ background: ACCENT_COLOR, boxShadow: `0 0 8px ${ACCENT_COLOR}` }}
            initial={{ left: "0%", opacity: 0 }}
            animate={active ? { left: ["0%", "100%"], opacity: [0, 1, 1, 1, 0] } : undefined}
            transition={{ duration: 2.8, delay, repeat: Infinity, ease: "linear" }}
          />
        ))}
      </div>

      {/* You endpoint */}
      <Endpoint className="left-3" icon={<Laptop size={18} aria-hidden="true" />} label="You" />
      {/* Target endpoint */}
      <Endpoint className="right-3" icon={<Globe size={18} aria-hidden="true" />} label="Target" />

      {/* ── 3D stage — isometric proxy slab, centered above the wire ──────── */}
      <div
        className="absolute inset-0 z-10 flex items-center justify-center"
        style={{ perspective: 1100 }}
      >
        <div
          className="relative h-[128px] w-[128px]"
          style={{
            transformStyle: "preserve-3d",
            transform: "rotateX(57deg) rotateZ(-38deg)",
          }}
        >
          {/* Ghost wireframe layers stacked beneath the slab */}
          <div className="absolute inset-0" style={{ transformStyle: "preserve-3d" }}>
            <div
              className="border-foreground/20 absolute inset-0 rounded-[18px] border border-dashed"
              style={{ transform: "translateZ(-24px)" }}
            />
            <div
              className="border-foreground/10 absolute inset-0 rounded-[18px] border border-dashed"
              style={{ transform: "translateZ(-40px)" }}
            />
          </div>

          {/* Floating slab */}
          <motion.div
            className="absolute inset-0"
            style={{ transformStyle: "preserve-3d" }}
            initial={{ z: 0 }}
            animate={active ? { z: [0, 2.5, 0, -2.5, 0] } : undefined}
            transition={{ duration: 4.4, repeat: Infinity, ease: "easeInOut" }}
          >
            {/* Drop shadow */}
            <div
              className="absolute -inset-2 rounded-[24px] bg-black/50 blur-lg"
              style={{ transform: "translateZ(-14px)" }}
            />
            {/* Underside */}
            <div
              className="absolute inset-0 rounded-[18px] bg-[#1a1d21]"
              style={{ transform: "translateZ(-10px)" }}
            />
            {/* Front edge wall */}
            <div
              className="absolute right-[16px] bottom-0 left-[16px] h-[10px]"
              style={{
                transformOrigin: "bottom",
                transform: "rotateX(90deg)",
                background: "linear-gradient(180deg, #3a3e44 0%, #1e2025 100%)",
              }}
            />
            {/* Right edge wall */}
            <div
              className="absolute top-[16px] right-0 bottom-[16px] w-[10px]"
              style={{
                transformOrigin: "right",
                transform: "rotateY(-90deg)",
                background: "linear-gradient(90deg, #2a2e33 0%, #16181c 100%)",
              }}
            />

            {/* Top face */}
            <div className="absolute inset-0 overflow-hidden rounded-[18px] border border-white/20 bg-[#0A0B0D]">
              <div className="absolute inset-0" style={{ backgroundColor: `${ACCENT_COLOR}0D` }} />
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage:
                    "linear-gradient(135deg, rgba(247,247,248,0.06), transparent 45%)",
                }}
              />

              {/* Shield mark + label + detail row (no IP) */}
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                <span
                  className="flex h-11 w-11 items-center justify-center rounded-2xl border"
                  style={{
                    color: ACCENT_COLOR,
                    borderColor: `${ACCENT_COLOR}40`,
                    background: `${ACCENT_COLOR}12`,
                  }}
                >
                  <ShieldCheck size={22} aria-hidden="true" />
                </span>
                <span className="font-mono text-[8px] tracking-[0.22em] text-white/70">PROXY</span>
                <div className="flex items-center gap-1">
                  <span className="h-[3px] w-3 rounded-full bg-white/15" />
                  <span className="h-[3px] w-3 rounded-full bg-white/15" />
                  <motion.span
                    className="h-[5px] w-[5px] rounded-full"
                    style={{ background: ACCENT_COLOR, boxShadow: `0 0 6px ${ACCENT_COLOR}` }}
                    initial={{ opacity: 0.4 }}
                    animate={active ? { opacity: [0.4, 1, 0.4] } : undefined}
                    transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                  />
                </div>
              </div>
            </div>

            {/* Breathing accent ring */}
            {!reduced && (
              <motion.div
                className="pointer-events-none absolute inset-0 rounded-[18px] border"
                style={{
                  borderColor: `${ACCENT_COLOR}59`,
                  boxShadow: `0 0 18px ${ACCENT_COLOR}2E, inset 0 0 14px ${ACCENT_COLOR}17`,
                }}
                initial={{ opacity: 0 }}
                animate={active ? { opacity: [0, 0.6, 0] } : undefined}
                transition={{ duration: 4.8, delay: 0.9, repeat: Infinity, ease: "easeInOut" }}
              />
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}

function Endpoint({
  className,
  icon,
  label,
}: {
  className: string;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <div
      className={`absolute top-[calc(50%+14px)] z-20 flex -translate-y-1/2 flex-col items-center gap-1.5 ${className}`}
    >
      <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/12 bg-[#16181c] text-white/75">
        {icon}
      </span>
      <span className="text-muted-foreground text-[10px] font-medium">{label}</span>
    </div>
  );
}
